import { FC, useMemo } from 'react'
import { useQuery } from 'react-query'
import { ModalSize, OptionsHelper, QUERIES } from '../../../../../../_metronic/helpers'
import { CustomSelect, IOption, useModal } from '../../../../../components'
import { BlocoAgenda, EscalaCalendario } from '../../components/EscalaCalendario'
import { EscalaAgendaDiaModal } from '../../components/EscalaAgendaDiaModal'
import { IndisponibilidadeGlobalModal } from '../../components/IndisponibilidadeGlobalModal'
import { EscalaAgendaViewEmpresa } from '../../core/_models'
import { getAgendaEmpresa } from '../../core/_requests'
import {
  estadoDaAgendaEmpresa,
  formatFaixaHorario,
  naoGerenciavelPelaEmpresa,
} from '../../core/escalaHelpers'
import { ParamsEscalaEmpresa, useQueryRequest } from '../core/QueryRequestProvider'

type Props = {
  empresa: number
}

export const EscalaEmpresaCalendario: FC<Props> = ({ empresa }) => {
  const { state, updateState } = useQueryRequest<ParamsEscalaEmpresa>()
  const { showModal } = useModal()

  const params = state.params ?? {}
  const { inicio, termino, especialidade } = params

  const {
    data: agenda,
    isFetching,
    refetch,
  } = useQuery(
    [QUERIES.ESCALAS_EMPRESA_AGENDA, empresa, inicio, termino, especialidade],
    () => getAgendaEmpresa(empresa, inicio!, termino!, especialidade),
    {
      enabled: Boolean(empresa && inicio && termino),
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  )

  /**
   * A resposta é `Map<PessoaList, Map<LocalDate, List<EscalaAgendaViewEmpresa>>>`.
   * A chave externa é o `PessoaList` serializado como chave de mapa pelo Jackson
   * (`toString()` do objeto) e **não deve ser interpretada** — o profissional vem
   * dentro de cada item, em `.pessoa`. Por isso achatamos os dois níveis.
   */
  const dias: EscalaAgendaViewEmpresa[] = useMemo(
    () => Object.values(agenda ?? {}).flatMap((porData) => Object.values(porData ?? {}).flat()),
    [agenda]
  )

  const blocos: BlocoAgenda[] = useMemo(
    () =>
      dias.map((dia) => {
        const global = naoGerenciavelPelaEmpresa(dia)
        return {
          id: String(dia.id),
          data: dia.data,
          horarioInicio: dia.horarioInicio,
          horarioTermino: dia.horarioTermino,
          estado: estadoDaAgendaEmpresa(dia),
          titulo: dia.pessoa?.nome,
          detalhe: global
            ? 'Registrada para todas as instituições'
            : dia.vinculos
                ?.map((vinculo) => vinculo.especialidade?.nome)
                .filter(Boolean)
                .join(', ') || undefined,
          // Bloco global: os endpoints de detalhe devolvem 403 (guardas
          // `valida*Empresa`), então a tela não oferece abrir nem editar.
          gerenciavel: !global,
        }
      }),
    [dias]
  )

  const abrirDia = (bloco: BlocoAgenda) => {
    const dia = dias.find((item) => String(item.id) === bloco.id)
    if (!dia) return

    showModal({
      title: `${dia.pessoa?.nome ?? 'Marcação'} — ${
        dia.dataFormatada ?? dia.data
      }, ${formatFaixaHorario(dia.horarioInicio, dia.horarioTermino)}`,
      size: ModalSize.Large,
      content: (
        <EscalaAgendaDiaModal eixo='empresa' empresa={empresa} agenda={dia} onSuccess={refetch} />
      ),
    })
  }

  const explicarBlocoGlobal = (bloco: BlocoAgenda) =>
    showModal({
      title: 'Indisponibilidade do profissional',
      size: ModalSize.Medium,
      content: <IndisponibilidadeGlobalModal bloco={bloco} />,
    })

  const atualizaParams = (novos: Partial<ParamsEscalaEmpresa>) =>
    updateState({ params: { ...params, ...novos } })

  return (
    <div className='card-body pt-0'>
      <div className='row mb-6'>
        <div className='col-md-4'>
          {/* `label` nativo envolvendo o select: dá nome acessível ao combobox
              do react-select, que o `CustomSelect` não expõe por conta própria. */}
          <label className='d-block'>
            <span className='fs-6 fw-bolder mb-2 d-block'>Especialidade</span>
            <CustomSelect
              name='especialidade'
              placeholder='Todas as especialidades'
              fetchOptions={OptionsHelper.getOptionsEspecialidades}
              isClearable
              onChange={(opcao: IOption | null) =>
                atualizaParams({ especialidade: opcao ? Number(opcao.value) : undefined })
              }
            />
          </label>
        </div>
      </div>

      <EscalaCalendario
        blocos={blocos}
        isLoading={isFetching}
        estadosNaLegenda={['disponivel', 'indisponivel', 'indisponibilidade-global']}
        onVisibleRangeChange={({ inicio: de, termino: ate }) =>
          atualizaParams({ inicio: de, termino: ate })
        }
        onSelecionar={abrirDia}
        onSelecionarNaoGerenciavel={explicarBlocoGlobal}
      />
    </div>
  )
}
